import React, { useState, useRef, useEffect } from "react";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import {
  FaFileAlt,
  FaFileAudio,
  FaFilePdf,
  FaFileVideo,
  FaPaperclip,
  FaPaperPlane,
  FaSmileBeam,
  FaStrikethrough,
} from "react-icons/fa";
import "react-quill/dist/quill.snow.css";
import { RiBold } from "react-icons/ri";
import { CgFormatItalic } from "react-icons/cg";
import { CiLink } from "react-icons/ci";

export default function ChatInput({ onSendMessage, loading, file, setFile }) {
  const editorRef = useRef("Type something...");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [pickerRef]);

  const handleInput = () => {
    const text = editorRef.current.innerText.trim();
    setIsEmpty(text === "");
  };

  const formatText = (command) => {
    document.execCommand(command, false, null);
    editorRef.current?.focus();
  };

  const handleSend = () => {
    const html = editorRef.current.innerHTML.trim();

    onSendMessage({ msg: html, file: file });
    editorRef.current.innerHTML = ""; // Clear input
    editorRef.current.focus();
    setIsEmpty(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const addEmoji = (emoji) => {
    const emojiChar = emoji.native;
    document.execCommand("insertText", false, emojiChar);
    // setShowEmojiPicker(false);
    editorRef.current?.focus();
  };

  function handleFileInput(event) {
    const selectedFile = event.target.files[0];

    const mimeType = selectedFile?.type;
    let typeCategory = "";
    if (mimeType.startsWith("image/")) {
      typeCategory = "image";
    } else if (mimeType.startsWith("audio/")) {
      typeCategory = "audio";
    } else if (mimeType.startsWith("video/")) {
      typeCategory = "video";
    } else if (mimeType === "application/pdf") {
      typeCategory = "document";
    } else {
      typeCategory = "file";
    }

    setFile((prevFiles) => [
      ...prevFiles,
      {
        file: selectedFile,
        type: typeCategory,
        preview: URL.createObjectURL(selectedFile),
      },
    ]);
  }

  return (
    <div className=" relative   m-4 rounded-xl shadow-lg space-y-2 max-w-[800px]  h-[800px] ">
      {/* Toolbar */}

      {showEmojiPicker && (
        <div
          ref={pickerRef}
          className="absolute bottom-32 -left-48 z-20 shadow-2xl rounded-xl border border-gray-200 bg-white"
        >
          <Picker
            data={data}
            onEmojiSelect={addEmoji}
            theme="light"
            previewPosition="none"
            skinTonePosition="none"
          />
        </div>
      )}

      <div className="relative w-full h-full  rounded-lg border  focus-within:ring-2 focus-within:ring-[#1C2638] transition-all duration-200">
        {/* Editable input */}
        <div className="p-3  min-h-[80px] max-h-[100px]  overflow-y-auto font-sans text-gray-800 text-base leading-relaxed">
          {/* Placeholder */}
          {isEmpty && (
            <span className="absolute left-4 top-3 text-gray-400 pointer-events-none select-none font-medium">
              Type your message...
            </span>
          )}

          {/* Editable Div */}
          <div
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            className="outline-none whitespace-pre-wrap break-words"
            suppressContentEditableWarning={true}
            onKeyDown={handleKeyDown}
          ></div>
          {file?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              {file.map((fileItem, idx) => {
                const { type, preview, file: fileData } = fileItem;
                const fileName = fileData?.get?.("file")?.name || "attachment";

                const getFileIcon = () => {
                  switch (type) {
                    case "video":
                      return <FaFileVideo className="text-black w-6 h-6" />;
                    case "audio":
                      return (
                        <FaFileAudio className="text-purple-500 w-6 h-6" />
                      );
                    case "document":
                      return <FaFilePdf className="text-red-500 w-6 h-6" />;
                    default:
                      return <FaFileAlt className="text-gray-600 w-6 h-6" />;
                  }
                };

                return (
                  <div
                    key={idx}
                    className="relative group w-14 flex flex-col items-center text-center"
                  >
                    {type === "image" ? (
                      <img
                        src={preview}
                        alt={fileName}
                        className="w-10 h-10 object-cover rounded-md"
                      />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-md">
                        {getFileIcon()}
                      </div>
                    )}
                    {/* 
                    <p className="text-[10px] truncate w-full mt-1">
                      {fileName}
                    </p> */}

                    {/* ❌ Remove Button */}
                    <button
                      onClick={() => {
                        setFile((prev) => prev.filter((_, i) => i !== idx));
                        if (file.length === 1) setIsEmpty(true);
                      }}
                      className="absolute -top-1 -right-1 bg-white text-black border border-gray-300 rounded-full w-4 h-4 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      title="Remove"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Send Button */}
        <div className="flex gap-2 p-3">
          <div className="flex items-center w-full gap-2 p-1 rounded-lg border border-gray-200">
            {/* Bold */}
            <button
              onClick={() => formatText("bold")}
              title="Bold"
              className="p-2 text-[#1C2638] rounded-lg hover:bg-gray-700/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <RiBold size={20} />
            </button>

            {/* Italic */}
            <button
              onClick={() => formatText("italic")}
              title="Italic"
              className="p-2 text-[#1C2638] rounded-lg hover:bg-gray-700/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <CgFormatItalic size={20} />
            </button>

            {/* Strikethrough */}
            <button
              onClick={() => formatText("strikeThrough")}
              title="Strikethrough"
              className="p-2 text-[#1C2638] rounded-lg hover:bg-gray-700/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <FaStrikethrough size={18} />
            </button>

            {/* Emoji */}
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              title="Emoji"
              className="p-2 text-[#1C2638] rounded-lg hover:bg-gray-700/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <FaSmileBeam size={18} />
            </button>

            {/* File Upload */}
            <div
              className={`relative p-2 rounded-lg  transition-all duration-200 ${
                file?.length < 5
                  ? "hover:bg-gray-700/50 text-[#1C2638]  cursor-pointer"
                  : "cursor-not-allowed text-gray-300"
              }`}
            >
              <input
                id="file"
                type="file"
                hidden
                onChange={file?.length < 5 ? handleFileInput : () => {}}
                accept=".jpeg,.png,.mp4,.3gp,.mp3, .aac,.amr,.m4a,.ogg,.txt, .xls, .xlsx, .doc, .docx, .pdf, .ppt, .pptx"
                multiple
              />

              <label
                htmlFor="file"
                className={` ${
                  file?.length < 5
                    ? "hover:bg-gray-700/50 text-[#1C2638]  cursor-pointer"
                    : "cursor-not-allowed text-gray-300"
                }`}
              >
                <FaPaperclip size={18} className="rotate-45" />
              </label>
            </div>
          </div>
          <button
            onClick={handleSend}
            disabled={loading}
            className="bg-gray-900 text-white px-6 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md"
          >
            <FaPaperPlane size={16} />
            {loading ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
